import LevelNodeComponent from '../../../Component/Map/LevelNode';
import TransformComponent from '../../../Component/Transform';
import Renderable from '../../../Component/Renderable';
import GameObject, { GameObjectEvent } from '../../../GameObject/GameObject';
import Sprite from '../../Sprite';
import Point from '../../../Utility/Point';
import Interactable from '../../../Component/Interactable';
import AnimationComponent from '../../../Component/Animation';
import Text from '../../Text';
import Game from '../../../Game';
import { WorldDataHelper } from '../../../Utility/WorldDataHelper';
import LevelNodeIndicator from './LevelNodeIndicator';
import { IGameState } from '../../../State/Game/IGameState';
import StarChest from '../../../Prefabs/StarChest/StarChest';
import { StarChestEvent, StarChestHelper } from '../../../Utility/StarChestHelper';
import { unexpectedValue } from '../../../Utility/Types';
import { StartAnimationClip } from '../../../System/Animation/AnimationHelper';
import { DragonbonesFadeIn, StarChestUpgrade, StarChestUpgradeParams } from '../../../System/Animation/Clip';
import StarChestStarsCount from '../../../Prefabs/StarChest/StarChestStarsCount';
import Reasons from '../../../Utility/Reasons';
import LevelNodeState from './LevelNodeState';
import LevelNodePreventCacheReasons from './LevelNodePreventCacheReasons';
import { LevelNodeType } from './LevelNodeType';
import Armature from '../../Armature';
import { addDragonbonesResources } from '../../../Utility/Resource/ResourceHelper';
import { DRAGONBONES_PATH } from '../../../Constants/Dragonbones';
import { UIActions } from '../../../State/UI/UIActions';
import { PopupID } from '../../../State/UI/PopupID';
import SpriteComponent from '../../../Component/Render/Sprite';

const DEFAULT_TEXTURE: string = 'blue';
const TEXT_NAME: string = 'level_number';
const IDLE_ARMATURE_NAME: string = 'idle_armature';
const DB_CONTAINER_NAME: string = 'db_container';

const idleDBName: string = 'level_node';
const idleDBArmatureName: string = 'Armature';
const idleDBAnimationName: string = 'animtion0';

const hardIdleDBName: string = 'hard_level_node';
const hardIdleDBArmatureName: string = 'Armature';
const hardIdleDBAnimationName: string = 'idle';

const petPositionOffset: Point = new Point(0, 0);

export default class LevelNode extends GameObject {
    public reasonsToNotCacheAsBitmap: Reasons<LevelNodePreventCacheReasons>;
    public levelNumber: number;

    private fieldId: string;
    private isCropUnlock: boolean;
    private isFieldUnlock: boolean;
    private unlockState: LevelNodeState;
    private baseSprite: Sprite;

    private isHardLevel: boolean;

    // This is the position on the node that the Pet would walk to (adjust as needed for special nodes)
    private petPosition: Point;

    public onCreate(params: {
        baseSprite: Sprite;
        number: number;
        levelNodeState: LevelNodeState;
        stars?: number;
        isStarChestLevel?: boolean;
        petId?: string;
        starChestTier?: number;
        starChestFTUXComplete?: boolean;
        starChestStarCount: number;
    }): void {
        const { stars = 0, isStarChestLevel = false, petId, starChestTier = 0, starChestFTUXComplete = false } = params;
        this.name = `levelnode_${params.number}`;
        this.levelNumber = params.number;
        this.unlockState = params.levelNodeState;
        this.baseSprite = params.baseSprite;
        this.isHardLevel = WorldDataHelper.isHardLevel(this.levelNumber);

        const interactable = this.addComponent(Interactable);
        this.addComponent(TransformComponent, params);
        this.addComponent(Renderable, params);
        this.addComponent(LevelNodeComponent, params);
        this.addComponent(AnimationComponent);

        let { baseTexture, textFill, strokeColor } = this.getStyle();

        this.getComponent(TransformComponent).position.copy(
            params.baseSprite.getComponent(TransformComponent).position
        );
        params.baseSprite.getComponent(SpriteComponent).texture = baseTexture;
        this.addChild(this.baseSprite);

        const dbContainer = GameObject.Instantiate(Sprite, {
            name: DB_CONTAINER_NAME,
            texture: 'blank',
            anchor: new Point(0.5, 0.5),
            scale: new Point(1, 1),
        });
        this.baseSprite.addChild(dbContainer);

        const text = GameObject.Instantiate(Text, {
            name: TEXT_NAME,
            position: new Point(-1, -18),
            anchor: new Point(0.5, 0.5),
            scale: new Point(1, 1),
            text: String(this.levelNumber),
            style: {
                ...Game.style.getStyle('level_node.level_number'),
                stroke: strokeColor,
                fill: textFill,
            },
            align: 'center',
            alpha: this.unlockState === LevelNodeState.FUTURE ? 0.5 : 1,
        });
        this.baseSprite.addChild(text);

        const nodeStarsParams = this.isHardLevel ? this.hardLevelStars : this.stars;
        nodeStarsParams.forEach((star: any, index: number) => {
            const sprite: Sprite = GameObject.Instantiate(Sprite, star);

            if (index <= stars - 1) {
                this.baseSprite.addChild(sprite);
            }
        });

        this.reasonsToNotCacheAsBitmap = new Reasons();

        if (isStarChestLevel) {
            const tierData = StarChestHelper.getChestTierData(starChestTier);
            if (tierData) {
                this.reasonsToNotCacheAsBitmap.add(LevelNodePreventCacheReasons.StarChestAnimations);
                // getBounds before adding the star chest or the bounds will be too large
                const bounds = this.container.getBounds();
                const hitAreaScale = 0.9;
                const hitArea = new PIXI.Circle(0, 0, (bounds.width / 2) * hitAreaScale);
                // Hit area is set here because once the StarChest is added the DB animation increases the hit area by quite a lot
                interactable.hitArea = hitArea;

                const chest: GameObject = GameObject.Instantiate(StarChest, {
                    name: 'star_chest',
                    layout: {
                        offsets: [0, 15, 0, 15],
                        anchors: [0, 0, 0, 0],
                        pivot: [0.5, 0.5],
                        scale: [1, 1],
                    },
                    tier: starChestTier,
                    playIdleAnimation: true,
                });
                chest.getComponent(Renderable).visible = starChestFTUXComplete;
                chest.addComponent(Interactable);
                chest.on(GameObjectEvent.POINTER_UP, () => {
                    UIActions.showPopup(PopupID.STAR_CHEST_INFO, {
                        levelNumber: this.levelNumber,
                    });
                });

                const starChestStars = GameObject.Instantiate(StarChestStarsCount, {
                    name: 'star_chest',
                    layout: {
                        anchors: tierData.nodeChestStarsAnchors,
                        scale: tierData.nodeChestStarsScale,
                    },
                    stars: params.starChestStarCount,
                });

                chest.addChild(starChestStars);

                Game.scene.getActiveScene().on(StarChestEvent.REWARD_STAR, params => {
                    starChestStars.updateStarsAmount(params.starCount);
                });

                this.addChild(chest);

                Game.scene.getActiveScene().on(StarChestEvent.UPGRADE_CHEST, (meta: StarChestUpgradeParams) => {
                    StartAnimationClip(
                        new StarChestUpgrade(chest, {
                            ...meta,
                            updateTexture: false,
                        })
                    );
                });
            }
        }

        if (this.isCropUnlock || this.isFieldUnlock) {
            const indicator: GameObject = this.createIndicator();
            this.baseSprite.addChild(indicator);
        }
        this.reasonsToNotCacheAsBitmap.addListener(hasAReason => {
            this.getComponent(Renderable).cache = !hasAReason;
        }, true);

        if (this.unlockState === LevelNodeState.CURRENT) {
            this.loadDBAssets().then(this.onDBAssetsLoaded);
            this.reasonsToNotCacheAsBitmap.add(LevelNodePreventCacheReasons.CurrentNode);
        }
        this.petPosition = petPositionOffset.clone();
    }

    public destroy() {
        super.destroy();
    }

    private createIndicator(): GameObject {
        let type: LevelNodeType;

        if (this.isCropUnlock) {
            type = LevelNodeType.CROP_UNLOCK;
        } else if (this.isFieldUnlock) {
            type = LevelNodeType.FIELD_UNLOCK;
        }

        return GameObject.Instantiate(LevelNodeIndicator, {
            x: 0,
            y: 0,
            unlockState: this.unlockState,
            fieldId: this.fieldId,
            type,
            scale: new Point(0.75, 0.75),
        });
    }

    private getStyle(): {
        strokeColor: string;
        textFill: string;
        baseTexture: string;
    } {
        const color: string = this.getNodeColor(this.levelNumber);
        const isHard = color === 'hard';

        switch (this.unlockState) {
            case LevelNodeState.CURRENT:
                return {
                    baseTexture: `node_current${isHard ? '_purple' : ''}`,
                    strokeColor: isHard ? '#8000af' : '#0b4452',
                    textFill: '#ffffff',
                };
            case LevelNodeState.FUTURE:
                return {
                    baseTexture: `node_future${isHard ? '_hard' : ''}`,
                    strokeColor: isHard ? '#8000af' : '#282333',
                    textFill: 'white',
                };
            case LevelNodeState.COMPLETE:
                return {
                    baseTexture: `${color}_node`,
                    strokeColor: isHard ? '#8000af' : '#262e90',
                    textFill: '#ffffff',
                };
            default:
                unexpectedValue(this.unlockState);
        }
    }

    private getNodeColor(levelNumber: number): string {
        const allFieldData = WorldDataHelper.getAllFieldsData();
        const field = allFieldData
            .slice(0)
            .sort((a, b) => b.requiredLevel[0] - a.requiredLevel[0])
            .find(fieldData => levelNumber >= fieldData.requiredLevel[0]);
        if (field) {
            this.isCropUnlock = this.levelNumber === field.requiredLevel[0];
            this.isFieldUnlock = this.levelNumber === field.requiredLevel[1];
            if (this.isCropUnlock || this.isFieldUnlock) {
                this.fieldId = field.id;
            }
        }

        if (this.isHardLevel) {
            return 'hard';
        }

        if (field) {
            if (this.isCropUnlock || this.isFieldUnlock) {
                return DEFAULT_TEXTURE;
            }

            return field.nodeTexture ? field.nodeTexture : DEFAULT_TEXTURE;
        }

        return DEFAULT_TEXTURE;
    }

    public getPetPosition(): Point {
        return Point.add(this.getComponent(TransformComponent).position.clone() as Point, this.petPosition.clone());
    }

    public getWheelPosition(): Point {
        return Point.add(this.getComponent(TransformComponent).position.clone() as Point, new Point(0, -50));
    }

    public getPlinkoIndicatorPosition(): Point {
        return Point.add(this.getComponent(TransformComponent).position.clone() as Point, new Point(0, -16));
    }

    protected stars: any[] = [
        {
            name: 'star_1',
            texture: 'level_node_star_1',
            position: new Point(-24, 6),
            anchor: new Point(0.5, 0.5),
            scale: new Point(1, 1),
        },
        {
            name: 'star_2',
            texture: 'level_node_star_2',
            position: new Point(0, 11),
            anchor: new Point(0.5, 0.5),
            scale: new Point(1, 1),
        },
        {
            name: 'star_3',
            texture: 'level_node_star_3',
            position: new Point(24, 6),
            anchor: new Point(0.5, 0.5),
            scale: new Point(1, 1),
        },
    ];

    protected hardLevelStars: any[] = [
        {
            name: 'star_1',
            texture: 'hard_level_node_star_1',
            position: new Point(-24, 6),
            anchor: new Point(0.5, 0.5),
            scale: new Point(0.6, 0.6),
        },
        {
            name: 'star_2',
            texture: 'hard_level_node_star_2',
            position: new Point(0, 11),
            anchor: new Point(0.5, 0.5),
            scale: new Point(0.6, 0.6),
        },
        {
            name: 'star_3',
            texture: 'hard_level_node_star_3',
            position: new Point(24, 6),
            anchor: new Point(0.5, 0.5),
            scale: new Point(0.6, 0.6),
        },
    ];

    public static getNodeState(levelNumber: number, state: IGameState = Game.store.getState()): LevelNodeState {
        const currentLevel: number = Game.player.getHighestLevelCompleted(state) + 1;

        if (currentLevel > levelNumber) return LevelNodeState.COMPLETE;
        if (currentLevel < levelNumber) return LevelNodeState.FUTURE;
        return LevelNodeState.CURRENT;
    }

    private async loadDBAssets(): Promise<void> {
        addDragonbonesResources(this.isHardLevel ? hardIdleDBName : idleDBName, `${DRAGONBONES_PATH}/LevelNode/`);
        await Game.resource.load();
    }

    private readonly onDBAssetsLoaded = () => {
        if (this.destroyed) return;

        const idleArmature = GameObject.Instantiate(Armature, {
            name: IDLE_ARMATURE_NAME,
            dragonbonesName: this.isHardLevel ? hardIdleDBName : idleDBName,
            armatureName: this.isHardLevel ? hardIdleDBArmatureName : idleDBArmatureName,
            animationName: this.isHardLevel ? hardIdleDBAnimationName : idleDBAnimationName,
            alpha: 0,
            playOnStart: false,
        });

        let targetAlpha: number = 0.4;
        if (this.getNodeColor(this.levelNumber) === 'hard') {
            // increase alpha for hard, since the anim is purple
            targetAlpha = 0.8;
        }

        this.DBContainer.addChild(idleArmature);

        StartAnimationClip(new DragonbonesFadeIn(idleArmature, { targetAlpha: targetAlpha }));
    };

    private get DBContainer(): GameObject | undefined {
        return this?.baseSprite?.find(DB_CONTAINER_NAME);
    }

    private get idleArmature(): Armature | undefined {
        const armature = this?.DBContainer?.find(IDLE_ARMATURE_NAME);
        return armature ? (armature as Armature) : undefined;
    }

    private get text(): GameObject | undefined {
        return this?.find(TEXT_NAME);
    }
}
